import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import TradeInPricingTabs from '../../components/TradeInPricingTabs';
import AlertNotification from '../../components/AlertNotification';
import SearchBox from '../../components/SearchBox';
import Pagination from '../../components/Pagination';
import FilterDrawer from '../../components/FilterDrawer';
import { formatDateTime } from '../../utils/formateDate';
import noBrandImage from '../../assets/img/brand/noImage.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import SortableHeader from '../../components/SortableHeader';
import apiHelper from '../../apiHelper';

// ─── Page size (history records per page) ────────────────────────────────────
const PH_PAGE_SIZE = 5;

// ─── Flatten grouped history records → flat item rows ────────────────────────
function flattenHistory(records) {
    return records.flatMap(record =>
        (record.items || []).map(item => ({
            historyId:     record.sales_listing_price_history_id,
            changesTime:   record.changes_time,
            initiator:     record.initiator,
            reason:        record.reason,
            spu:           item.spu,
            sku:           item.sku,
            skuImage:      item.sku_image,
            condition:     item.condition,
            conditionType: item.condition_type,
            oldPrice:      parseFloat(item.base_price_before) || 0,
            newPrice:      parseFloat(item.base_price_after)  || 0,
            platforms:     item.platforms || [],
        }))
    );
}

// ─── Cache key & params builders ─────────────────────────────────────────────
function buildPHCacheKey(page, search, filter, sf, so) {
    return JSON.stringify({
        page,
        search: (search || '').trim(),
        filter: filter ? {
            start:     filter.changesTime_start ? new Date(filter.changesTime_start).toISOString() : null,
            end:       filter.changesTime_end   ? new Date(filter.changesTime_end).toISOString()   : null,
            condition: (filter.condition || []).slice().sort(),
        } : null,
        sf: sf || null,
        so,
    });
}

function buildPHParams(page, search, filter, sf, so) {
    const params = new URLSearchParams({ page, page_size: PH_PAGE_SIZE });
    const s = (search || '').trim();
    if (s) params.append('search', s);
    if (filter?.changesTime_start) params.append('changes_time_start', new Date(filter.changesTime_start).toISOString());
    if (filter?.changesTime_end)   params.append('changes_time_end',   new Date(filter.changesTime_end).toISOString());
    if (filter?.condition?.length) params.append('condition', filter.condition.join(','));
    if (sf) {
        const FIELD_MAP = { time: 'modified_at', initiator: 'modified_by__first_name', stockName: 'spu_name' };
        const dbField = FIELD_MAP[sf];
        if (dbField) params.append('ordering', `${so === 'desc' ? '-' : ''}${dbField}`);
    } else {
        params.append('ordering', '-time');
    }
    return params.toString();
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function PhoneImage({ src, size = 32 }) {
    return (
        <img
            src={src || noBrandImage}
            alt=""
            style={{ width: size, height: size, objectFit: 'cover', flexShrink: 0, border: '1px solid #E9ECEF', background: '#F1F3F5' }}
        />
    );
}

const FilterInactiveIcon = () => (
    <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const FilterActiveIcon = () => (
    <svg style={{ margin: 0 }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <g clipPath="url(#clip_ph_test_fa)">
            <path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" />
        </g>
        <defs>
            <clipPath id="clip_ph_test_fa">
                <rect width="12" height="12" fill="white" transform="translate(11 5)" />
            </clipPath>
        </defs>
    </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
export default function PricingHistoryTest() {
    const location = useLocation();
    const [notification, setNotification] = useState({ show: false, message: '' });

    useEffect(() => {
        if (location.state?.successMessage) {
            setNotification({ show: true, message: location.state.successMessage });
            window.history.replaceState({}, document.title);
        }
    }, []); // eslint-disable-line

    // ── Data state ──
    const [rows,       setRows]       = useState([]);   // flattened item rows
    const [totalCount, setTotalCount] = useState(0);    // total history records
    const [isLoading,  setIsLoading]  = useState(true);

    // ── Search ──
    const [phSearchInput,   setPhSearchInput]   = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // ── Pagination ──
    const [phCurrentPage, setPhCurrentPage] = useState(1);

    // ── Filter ──
    const [phAppliedFilter,   setPhAppliedFilter]   = useState(null);
    const [phFilterPopupOpen, setPhFilterPopupOpen] = useState(false);
    const [phTempValues,      setPhTempValues]      = useState({
        changesTime_start: null,
        changesTime_end:   null,
        condition:         [],
    });

    // ── Sort ──
    const [phSortField, setPhSortField] = useState('time');
    const [phSortOrder, setPhSortOrder] = useState('desc');

    // ── Prefetch cache refs ──
    const prefetchCache      = useRef(new Map());
    const prefetchInProgress = useRef(new Set());
    const isFirstRender      = useRef(true);

    // ── Drawer ──
    const [selectedRow, setSelectedRow] = useState(null);
    const [drawerOpen,  setDrawerOpen]  = useState(false);
    const drawerRef = useRef(null);

    const isPhInitialTableEmpty = !isLoading && totalCount === 0 && !debouncedSearch && !phAppliedFilter;
    const phTotalPages = Math.ceil(totalCount / PH_PAGE_SIZE);

    const phSections = [
        { label: 'Changes Time', type: 'dateRangeWithTime', key: 'changesTime' },
        { label: 'Condition', type: 'checkbox', key: 'condition', options: ['New', 'Used'] },
    ];

    // ── Sort handler ──
    const handlePhSort = (col) => {
        if (phSortField === col) {
            setPhSortOrder(o => o === 'asc' ? 'desc' : 'asc');
        } else {
            setPhSortField(col);
            setPhSortOrder('asc');
        }
        setPhCurrentPage(1);
    };

    // ── Debounce search ──
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(phSearchInput);
            setPhCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [phSearchInput]);

    // ── Clear cache on filter/sort change ──
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }
        prefetchCache.current.clear();
        prefetchInProgress.current.clear();
    }, [debouncedSearch, phAppliedFilter, phSortField, phSortOrder]);

    // ── Prefetch helper ──
    const prefetchPHPage = async (page, search, filter, sf, so, totalCnt) => {
        const totalPgs = Math.ceil(totalCnt / PH_PAGE_SIZE);
        if (page > totalPgs) return;
        const key = buildPHCacheKey(page, search, filter, sf, so);
        if (prefetchCache.current.has(key) || prefetchInProgress.current.has(key)) return;
        prefetchInProgress.current.add(key);
        try {
            const res = await apiHelper(`sales/pricing-history/?${buildPHParams(page, search, filter, sf, so)}`);
            if (res?.ok) {
                const data = await res.json();
                prefetchCache.current.set(key, {
                    rows:  flattenHistory(data.results ?? []),
                    count: data.count ?? 0,
                });
            }
        } catch (_) {}
        finally { prefetchInProgress.current.delete(key); }
    };

    // ── Cache-aware fetch ──
    const fetchPHPage = async (page, search, filter, sf, so) => {
        const key    = buildPHCacheKey(page, search, filter, sf, so);
        const cached = prefetchCache.current.get(key);
        if (cached) {
            setRows(cached.rows);
            setTotalCount(cached.count);
            prefetchPHPage(page + 1, search, filter, sf, so, cached.count);
            return;
        }
        setIsLoading(true);
        try {
            const res = await apiHelper(`sales/pricing-history/?${buildPHParams(page, search, filter, sf, so)}`);
            if (res?.ok) {
                const data  = await res.json();
                const flat  = flattenHistory(data.results ?? []);
                const count = data.count ?? 0;
                prefetchCache.current.set(key, { rows: flat, count });
                setRows(flat);
                setTotalCount(count);
                prefetchPHPage(page + 1, search, filter, sf, so, count);
            } else {
                setRows([]); setTotalCount(0);
            }
        } catch (_) {
            setRows([]); setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Reactive fetch ──
    useEffect(() => {
        fetchPHPage(phCurrentPage, debouncedSearch, phAppliedFilter, phSortField, phSortOrder);
    }, [phCurrentPage, debouncedSearch, phAppliedFilter, phSortField, phSortOrder]); // eslint-disable-line

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="company-body new-look stock-wrap bg-checkbox">
            <TradeInPricingTabs />
            <div className="employee-tabs-container">
                {notification.show && (
                    <AlertNotification
                        type="success"
                        recordName=""
                        action="create"
                        message={notification.message}
                        onClose={() => setNotification({ show: false, message: '' })}
                    />
                )}
                <div className="tab-content" id="price-history-test-content">
                    <div className="info-section">
                        <div className="info-card">
                            <div className="entities-tab">
                                <div className="toolbar">
                                    <div className="toolbar-left">
                                        <SearchBox
                                            searchInput={phSearchInput}
                                            setSearchInput={setPhSearchInput}
                                            setSearchTerm={setDebouncedSearch}
                                            setCurrentPage={setPhCurrentPage}
                                            disabled={isPhInitialTableEmpty}
                                        />
                                        <button
                                            className={`icon-btn p-2 filter-btn ${phAppliedFilter ? 'applied' : ''}`}
                                            onClick={() => !isPhInitialTableEmpty && setPhFilterPopupOpen(prev => !prev)}
                                            disabled={isPhInitialTableEmpty}
                                            style={{
                                                border: phAppliedFilter ? '1px solid #2C7BE5' : '1px solid #6C757D',
                                                borderWidth: '1px',
                                                color: phAppliedFilter ? '#2C7BE5' : '#6C757D',
                                                background: isPhInitialTableEmpty ? '#E9ECEF' : phAppliedFilter ? '#CFE2FF' : '#FFFFFF',
                                                height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                opacity: isPhInitialTableEmpty ? 0.6 : 1,
                                                pointerEvents: isPhInitialTableEmpty ? 'none' : 'auto',
                                                cursor: isPhInitialTableEmpty ? 'not-allowed' : 'pointer',
                                                borderRadius: '8px',
                                            }}
                                        >
                                            {phAppliedFilter && !isPhInitialTableEmpty ? <FilterActiveIcon /> : <FilterInactiveIcon />}
                                        </button>
                                        <FilterDrawer
                                            isOpen={phFilterPopupOpen}
                                            onClose={() => setPhFilterPopupOpen(false)}
                                            sections={phSections}
                                            tempValues={phTempValues}
                                            setTempValues={setPhTempValues}
                                            onApply={() => {
                                                const hasTime = phTempValues.changesTime_start || phTempValues.changesTime_end;
                                                const hasCond = phTempValues.condition?.length > 0;
                                                setPhAppliedFilter(hasTime || hasCond ? {
                                                    changesTime_start: phTempValues.changesTime_start,
                                                    changesTime_end:   phTempValues.changesTime_end,
                                                    condition:         phTempValues.condition,
                                                } : null);
                                                setPhCurrentPage(1);
                                                setPhFilterPopupOpen(false);
                                            }}
                                            onClear={() => {
                                                setPhTempValues({ changesTime_start: null, changesTime_end: null, condition: [] });
                                                setPhAppliedFilter(null);
                                            }}
                                            width={400}
                                        />
                                    </div>
                                </div>

                                <div className="table-wrapper" style={{ border: 'none' }}>
                                    {isLoading ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 16, fontFamily: 'Montserrat' }}>
                                            <div style={{ width: 36, height: 36, border: '4px solid #f3f3f3', borderTop: '4px solid #FFEB00', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>Loading history...</span>
                                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                                        </div>
                                    ) : isPhInitialTableEmpty ? (
                                        <div className="empty-state">
                                            <div className="empty-state-content">
                                                <div className="icon">
                                                    <img src={noBrandImage} alt="avatar" className="avatar" style={{ width: 120, height: 120, verticalAlign: 'middle' }} />
                                                </div>
                                                <h1 className="no-item-title mt-2">No Pricing History Found</h1>
                                                <p className="no-item-text">Add a Listing first to initiate Pricing</p>
                                            </div>
                                        </div>
                                    ) : !isLoading && rows.length === 0 ? (
                                        <div className="empty-state">
                                            <div className="empty-state-content">
                                                <div className="icon">
                                                    <img src={emptySearchImg} alt="avatar" className="avatar" style={{ width: 120, height: 120, verticalAlign: 'middle' }} />
                                                </div>
                                                <h1 className="no-item-title mt-2">No History Found</h1>
                                                <p className="no-item-text">No results match what you're looking for</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <table className="entities-table table table-striped">
                                            <thead>
                                                <tr style={{ height: '45px', verticalAlign: 'middle' }}>
                                                    <SortableHeader label="Changes Time"      field="time"             sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort} />
                                                    <SortableHeader label="Initiator"         field="initiator"        sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort} />
                                                    <SortableHeader label="Listing Model"     field="stockName"        sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort} />
                                                    <SortableHeader label="SKU"               field="sku"              sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort} />
                                                    <SortableHeader label="Condition"         field="condition"        sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort} />
                                                    <SortableHeader label="Base Price Change" field="basePriceChanges" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort} />
                                                    <th style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529' }}>Platform Prices</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((row, idx) => {
                                                    const isIncrease = row.newPrice >= row.oldPrice;
                                                    const diff = Math.abs(row.newPrice - row.oldPrice);
                                                    return (
                                                        <tr key={`${row.historyId}-${idx}`}>
                                                            {/* 1. Changes Time */}
                                                            <td>
                                                                <span style={{ whiteSpace: 'nowrap', fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat' }}>
                                                                    {formatDateTime(row.changesTime)}
                                                                </span>
                                                            </td>

                                                            {/* 2. Initiator */}
                                                            <td style={{ whiteSpace: 'nowrap', fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat' }}>
                                                                {row.initiator || '—'}
                                                            </td>

                                                            {/* 3. Listing Model (SPU) */}
                                                            <td>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                    <PhoneImage src={row.skuImage} />
                                                                    <span style={{ whiteSpace: 'nowrap', fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat' }}>
                                                                        {row.spu || '—'}
                                                                    </span>
                                                                </div>
                                                            </td>

                                                            {/* 4. SKU */}
                                                            <td>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                    <PhoneImage src={row.skuImage} />
                                                                    <span style={{ whiteSpace: 'nowrap', fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat' }}>
                                                                        {row.sku || '—'}
                                                                    </span>
                                                                </div>
                                                            </td>

                                                            {/* 5. Condition */}
                                                            <td>
                                                                <span style={{
                                                                    height: '20px', display: 'flex', minWidth: '72px',
                                                                    alignItems: 'center', justifyContent: 'center',
                                                                    padding: '5px 10px', borderRadius: '99px',
                                                                    border: row.condition === 'New' ? '1px solid #FFEB00' : '1px solid #ADB5BD',
                                                                    background: row.condition === 'New' ? '#FFEB00' : '#E9ECEF',
                                                                    color: '#212529', fontWeight: 600, fontSize: 12,
                                                                    whiteSpace: 'nowrap', fontFamily: 'Montserrat',
                                                                }}>
                                                                    {row.condition || '—'}
                                                                </span>
                                                            </td>

                                                            {/* 6. Base Price Change */}
                                                            <td>
                                                                <div style={{ whiteSpace: 'nowrap', fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat', color: '#212529' }}>
                                                                    SGD {row.oldPrice.toLocaleString('en-SG', { minimumFractionDigits: 2 })}
                                                                    {' → '}
                                                                    SGD {row.newPrice.toLocaleString('en-SG', { minimumFractionDigits: 2 })}
                                                                </div>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, fontWeight: 400, color: isIncrease ? '#13795B' : '#B02A37' }}>
                                                                    {isIncrease
                                                                        ? <svg xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none"><path d="M6 0.75L9.5 7.875L6 6.375L2.5 7.875L6 0.75Z" fill="#13795B" stroke="#13795B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                        : <svg xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none" style={{ transform: 'rotate(180deg)' }}><path d="M6 0.75L9.5 7.875L6 6.375L2.5 7.875L6 0.75Z" fill="#DC3545" stroke="#DC3545" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                    }
                                                                    SGD {diff.toLocaleString('en-SG', { minimumFractionDigits: 2 })}
                                                                </div>
                                                            </td>

                                                            {/* 7. Platform Prices */}
                                                            <td>
                                                                {row.platforms.length > 0 ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => { setSelectedRow(row); setDrawerOpen(true); }}
                                                                        style={{
                                                                            background: 'none', border: 'none', padding: 0,
                                                                            color: '#0D6EFD', fontFamily: 'Montserrat',
                                                                            fontSize: 13, fontWeight: 500, cursor: 'pointer',
                                                                            textDecoration: 'underline', whiteSpace: 'nowrap',
                                                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                                                        }}
                                                                    >
                                                                        View Details
                                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                            <polyline points="15 3 21 3 21 9" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                            <line x1="10" y1="14" x2="21" y2="3" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                    </button>
                                                                ) : (
                                                                    <span style={{ fontSize: 12, color: '#ADB5BD', fontFamily: 'Montserrat' }}>—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* ── Pagination ── */}
                                {totalCount > 0 && (
                                    <div className="pagination" style={{ marginTop: 16 }}>
                                        <div className="pagination-info">
                                            {`Page ${phCurrentPage} of ${phTotalPages} (${totalCount} record${totalCount !== 1 ? 's' : ''})`}
                                        </div>
                                        <Pagination
                                            currentPage={phCurrentPage}
                                            totalPages={phTotalPages}
                                            onPageChange={setPhCurrentPage}
                                        />
                                    </div>
                                )}

                                {/* ── Platform Prices Drawer ── */}
                                {drawerOpen && selectedRow && (
                                    <>
                                        <div className="drawer-overlay" onClick={() => { setDrawerOpen(false); setSelectedRow(null); }} />
                                        <div className="entity-drawer" ref={drawerRef} style={{ maxWidth: 680 }}>
                                            <div className="drawer-header" style={{ background: '#F8F9FA' }}>
                                                <h3>Platform Price Changes</h3>
                                                <button className="close-btn" onClick={() => { setDrawerOpen(false); setSelectedRow(null); }}>
                                                    <X size={18} />
                                                </button>
                                            </div>
                                            <div className="main-drawer-inner">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: '1px solid #F1F3F5' }}>
                                                    <PhoneImage src={selectedRow.skuImage} size={44} />
                                                    <div>
                                                        <div style={{ fontWeight: 700, fontSize: 18, fontFamily: 'Montserrat', color: '#212529' }}>{selectedRow.spu}</div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                                                            <span style={{ fontSize: 13, color: '#6C757D', fontFamily: 'Montserrat' }}>{selectedRow.sku}</span>
                                                            <span style={{
                                                                background: selectedRow.condition === 'New' ? '#FFEB00' : '#E9ECEF',
                                                                border: selectedRow.condition === 'New' ? '1px solid #FFEB00' : '1px solid #ADB5BD',
                                                                color: '#212529', fontWeight: 700, fontSize: 11,
                                                                padding: '2px 8px', borderRadius: 20, fontFamily: 'Montserrat',
                                                            }}>
                                                                {selectedRow.condition}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ padding: '0 0 20px', overflowX: 'auto' }}>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat', fontSize: 13 }}>
                                                        <thead>
                                                            <tr style={{ background: '#F8F9FA', borderBottom: '1px solid #E9ECEF' }}>
                                                                <th style={{ padding: '10px 20px', fontWeight: 600, color: '#495057' }}>Platform</th>
                                                                <th style={{ padding: '10px 16px', fontWeight: 600, color: '#495057' }}>Price Before</th>
                                                                <th style={{ padding: '10px 16px', fontWeight: 600, color: '#495057' }}>Price After</th>
                                                                <th style={{ padding: '10px 16px', fontWeight: 600, color: '#495057' }}>Modified At</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedRow.platforms.map((p, i) => (
                                                                <tr key={i} style={{ borderBottom: '1px solid #F1F3F5' }}>
                                                                    <td style={{ padding: '12px 20px', color: '#212529', fontWeight: 600 }}>{p.platform}</td>
                                                                    <td style={{ padding: '12px 16px', color: '#212529' }}>
                                                                        {p.platform_price_before != null ? `SGD ${parseFloat(p.platform_price_before).toLocaleString('en-SG', { minimumFractionDigits: 2 })}` : '—'}
                                                                    </td>
                                                                    <td style={{ padding: '12px 16px', color: '#212529' }}>
                                                                        {p.platform_price_after != null ? `SGD ${parseFloat(p.platform_price_after).toLocaleString('en-SG', { minimumFractionDigits: 2 })}` : '—'}
                                                                    </td>
                                                                    <td style={{ padding: '12px 16px', color: '#6C757D', whiteSpace: 'nowrap' }}>
                                                                        {p.modified_at ? formatDateTime(p.modified_at) : '—'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
